import { Action, createBrowserHistory } from 'history'
import $ from 'jquery'
import Cookies from 'js-cookie'
import debounce from 'lodash/debounce'
import includes from 'lodash/includes'
import isNil from 'lodash/isNil'
import isString from 'lodash/isString'
import startsWith from 'lodash/startsWith'
import OLGeomPoint from 'ol/geom/Point'
import { reduxStore } from 'reduxStore'

import { setActiveTab, setActiveTabBackgroundLoad } from 'api/actions'
import * as Model from 'api/model'
import * as PoziUtils from 'api/utils'
import { setTabLayersFilter, setTabLayersFilterDisabled } from 'drawer/tabs/layers/search/actions'
import { setMapX, setMapY, setMapZ } from 'map/actions'
import { getMap } from 'utils/MapUtils'
import { parseHash } from 'utils/url'

export const ADD_ALL_STATE_TO_URL = 'ADD_ALL_STATE_TO_URL'

// The history object
export let history

export let historyUpdated = false

/**
 * Pozi wrapper for ReactTraining History module.
 * https://github.com/ReactTraining/history
 */

function InitDrawerLoading () {
  window.delayLayerControlRenderOnFirstLoad = true
  Model.on('app.drawer.activetabid', function LegacyUpdateReactActiveTab (value) {
    if (window.delayLayerControlRenderOnFirstLoad) {
      // Delay initial drawer load to give the map a chance to load first for a better user experience.
      window.setTimeout(function () {
        window.store.dispatch(setActiveTab(value))
      }, 1000)
    } else {
      window.store.dispatch(setActiveTab(value))
    }
    window.delayLayerControlRenderOnFirstLoad = false
  })

  const bConfigInState = Model.getState('config', false)
  if (bConfigInState) {
    console.log('Skipping background render on layers for config mode... open layer control for render. Can be very slow for a large layer count.')
  } else {
  // If the page has fully loaded, don't wait any more to set active tab.
  // AND if the layer control has not yet loaded, do a background load.
    window.setTimeout(function () {
      window.delayLayerControlRenderOnFirstLoad = false
      window.store.dispatch(setActiveTabBackgroundLoad('layers'))
    }, 2000)
  }

  console.debug('History - set the search filter.')

  let tabLayersFilter = Model.getState('filter', '')
  if (tabLayersFilter === '') {
    const filterCookie = Cookies.get('pozi-state-filter')
    let filterCookieJson
    try {
      filterCookieJson = JSON.parse(filterCookie)
    } catch (error) {
      console.log('History - cannot retrieve filter from cookies: invalid JSON', filterCookie)
    }

    if (!isNil(filterCookieJson)) {
      tabLayersFilter = filterCookieJson
    }
  }
  if (tabLayersFilter !== '') {
    window.store.dispatch(setTabLayersFilter(tabLayersFilter))
  }
}

/**
 * Initialise History object
 */
export function HistoryInit () {
  if (history) {
    console.log('HistoryInit - history already initialsed. Not doing anything.')
    return
  }

  // Handle 'reload' state by forcing a page reload
  const onReloadStateChange = async (value, oldValue) => {
    console.debug('onReloadStateChange', value, oldValue)

    // Stop listening to the 'reload' state since the 'unset' below would trigger this function being called again
    Model.un('app.state.reload', modelObserverId)

    // Remove 'reload' from state
    Model.unset('app.state.reload')

    // Update the URL accordingly
    HistoryUpdateURL(true)

    // Allow URL to update
    await PoziUtils.sleep(500)

    // Reload the page
    window.location.reload()
  }

  const modelObserverId = Model.on('app.state.reload', onReloadStateChange)

  // Create history instance
  history = createBrowserHistory()

  // If action is not initiated by app (POP), then update state based on changed URL
  history.listen(({ action, location }) => {
    console.info('History has changed', action, location)
    // A POP action, means a 'passive' change, a hash changes through a back/forward button press for example
    if (action === Action.Pop) {
      // Update the app state with the new hash (disabled until we can handle state changes from URL)
      Model.setStateFromHash()

      console.info('History change caused new state!')
    } else {
      console.info('History action was not POP. Not setting state')
    }
  })

  // On initial load, build app state from url parameters
  Model.setStateFromUrl() // Parse hash and search params

  InitDrawerLoading()

  console.debug('Set the filter disabled flag.')

  const tabLayersFilterDisabled = Model.getState('filterdisabled', false)
  let btabLayersFilterDisabled = tabLayersFilterDisabled
  if (isString(tabLayersFilterDisabled)) {
    btabLayersFilterDisabled = tabLayersFilterDisabled === 'true'
  } else {
    btabLayersFilterDisabled = false
  }

  window.store.dispatch(setTabLayersFilterDisabled(btabLayersFilterDisabled))
}

/**
 * Update the appstate and encode the new appstate into the location hash
 * Can be based on map move.. so options might be an OL object... confusing right.
 */
export function HistoryUpdate (replace = false) {
  // console.log('Update app state')

  const currentWindowMode = Model.get('app.event.window.mode', '')
  const browserMaxPixelWidthMobileMode = 600 // See main.css @media (max-width: 600px)
  const browserWindowWidth = $(window).width() // returns width of browser viewport
  if (browserWindowWidth > browserMaxPixelWidthMobileMode) {
    if (currentWindowMode !== 'desktop') {
      Model.set('app.event.window.mode', 'desktop') // >=601 === Desktop mode
    }
  } else {
    if (currentWindowMode !== 'mobile') {
      Model.set('app.event.window.mode', 'mobile')
    }
  }

  const map = getMap()

  if (!map) {
    console.log('Unable to update history: map not initialised.')
    return
  }

  const mview = map.getView()
  const mcenter = mview.getCenter()
  const mcenterPoint = new OLGeomPoint(mcenter)
  const mcenterPointTransformed = mcenterPoint.transform('EPSG:3857', 'EPSG:4326')
  const mcenterPointCoords = mcenterPointTransformed.getCoordinates()

  if (!isNaN(mcenterPointCoords[0])) {
    const center = mcenterPointCoords
    const currentX = Model.getState('x', 0)
    if (currentX !== center[0].toFixed(5)) {
      Model.setState('x', null, center[0].toFixed(5))
    }
    window.store.dispatch(setMapX(center[0].toFixed(5)))

    const currentY = Model.getState('y', 0)
    if (currentY !== center[1].toFixed(5)) {
      Model.setState('y', null, center[1].toFixed(5))
    }
    window.store.dispatch(setMapY(center[1].toFixed(5)))
  }

  const zoomlevel = map.getView().getZoom()
  if (zoomlevel) {
    const currentZ = Model.getState('z', 0) * 1
    if (currentZ !== zoomlevel) {
      Model.setState('z', null, zoomlevel)
    }
    window.store.dispatch(setMapZ(zoomlevel))
  }

  Model.unset('app.state.layers')

  // Build a string of all currently visible layers
  const layerConfigs = Model.get('config.layers', [])

  layerConfigs.forEach(layerConf => {
    if (!layerConf) {
      console.log('Unable to get layer conf')
      return
    }

    const { titleid } = layerConf

    if (!titleid) {
      console.log('Invalid titleid', layerConf)
      return
    }

    // Catalogue layers are to be excluded
    const isCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)
    if (isCatalogue) {
      // console.debug('HistoryUpdate - layer is of catalogue type. Skipping.', titleid, layerConf)
      return
    }

    // Group layers (usually with children) are to be excluded. These are branch nodes of layertree.
    const layerHasChildren = !!layerConf.layers
    if (layerHasChildren) {
      // console.debug('HistoryUpdate - layer is branch node of layer tree. Skipping.', titleid, layerConf)
      return
    }

    // Use both the current visibility and the configured initial visibility
    const olLayer = PoziUtils.getOpenLayersObjectFromTitleID(titleid)

    if (!olLayer) {
      // console.debug('HistoryUpdate - OL layer not found. That is OK. Could be address/search layer.', titleid)
      return
    }

    // If the (ol)layer is currently visible, add it to the state
    const layerVisible = olLayer.getVisible()
    if (layerVisible) {
      const olLayerTitle = olLayer.get('title')
      if (olLayerTitle.startsWith('http')) {
        console.debug('HistoryUpdate - special case for external layers. Skip adding titleid (no point, no config for external layers)')
        return
      }

      const layers = Model.get('app.state.layers', [])
      if (!includes(layers, titleid)) {
        Model.setState('layers', null, titleid)
        Model.set('app.config.layerbytitleid[' + titleid + '].visible', true)
      } else {
        Model.unset('app.config.layerbytitleid[' + titleid + '].visible')
      }
    } else {
      Model.unset('app.config.layerbytitleid[' + titleid + '].visible')
    }
  })

  HistoryUpdateURL(replace)
}

export function GetHistoryURL (bAllState) {
  const hashStr = getHashString(bAllState)
  return window.location.origin + window.location.pathname + '#' + hashStr
}

/**
 * Push a new entry onto the history stack with a query string
 * and some state. Location state does not appear in the URL.
 * @param {json} options
 */
/* const listen = function () {
    const history = Model.get('app.history', null)
    history.listen(function (location, action) {
      console.log('app.history.location', location)
      // console.log('The last navigation action was', action)
    })
  } */

/**
 * Get object as unencoded query string.
 * @param {json} options
 * @return {string} URL search string
 */
/* const getQueryString = function (options) {
    const arr = []
    for (const key in options) {
      arr.push(key + '=' + options[key])
    }
    if (arr.length > 0) {
      return '?' + arr.join('&')
    } else {
      return ''
    }
  } */

/**
 * Get object as unencoded hash string.
 *
 * Options:   click: "141.27953,-37.02801"
 *            lat: "-37.07015"
 *            lon: "141.22495"
 *            site: "westwimmera"
 *            z: 12
 *
 * Return format example:
 *  #/site[bendigo]/z[18]/cen[lat,-36.34]/cen[lon,144.2]/layers[councils]/ls[councils,style3]/lo[councils,0.5]/click[lat,-36.6]/click[lon,144.5]
 *
 * @param {json} options
 * @return {string} URL hash string
 */
const getHashString = function (bAllState = '') {
  const appstate = Model.get('app.state', null)

  // console.debug('getHashString', bAllState, appstate)

  if (Model.get('app.localhost', false)) {
    const sitename = Model.get('app.site', 'public')
    const currentSite = Model.getState('site', '')
    if (currentSite !== sitename) {
      Model.setState('site', null, sitename)
    }
  }

  const arr = []
  let appStateItem = []
  let bAddToHash = true
  let layerConf

  for (const parentKey in appstate) {
    appStateItem = appstate[parentKey] // Should always return an array.
    if (bAllState !== ADD_ALL_STATE_TO_URL) {
      switch (parentKey) {
        case 'annotation':
          bAddToHash = false
          break
        default:
          bAddToHash = true
      }
    }

    if (bAddToHash) {
      for (const itemKey in appStateItem) {
        let hashKey = ''
        let hashValue = ''

        if (typeof appStateItem[itemKey] === 'object') {
          const itemObj = appStateItem[itemKey]
          for (const objKey in itemObj) {
            hashKey = objKey
            hashValue = itemObj[objKey]
          }
        } else {
          hashKey = ''
          hashValue = appStateItem[itemKey]
        }

        if (hashValue === '') {
          continue
        }

        if (parentKey === 'layers') {
          let hashValueTitleID = hashValue
          if (includes(hashValue, 'http')) {
            hashValueTitleID = PoziUtils.getPoziIDFromString(hashValue)
          }

          layerConf = PoziUtils.getLayerFromTitleID(hashValueTitleID)

          const { groupid, showInLayerControl = true, titleid } = layerConf || {}

          // Ignore layers that do no show up in the layer control
          if (!showInLayerControl) {
            continue
          }

          // Do not add basemap layer to the URL hash if it's the first basemap layer
          if (groupid === 'basemap') {
            const groupsState = reduxStore.getState().groups
            const basemapGroupLayers = groupsState.basemap?.layers || []

            // It is possible that there are catalogue layers in the group. We will ignore them.
            const firstNonCatalogueLayer = basemapGroupLayers.find(basemapTitleid => {
              if (basemapTitleid === titleid) {
                return true
              }

              const layerConf = PoziUtils.getLayerFromTitleID(basemapTitleid)
              const isCatalogue = PoziUtils.isLayerTypeCatalogue(layerConf)

              return !isCatalogue
            })

            console.debug('getHashString - firstNonCatalogueLayer', firstNonCatalogueLayer, titleid)

            if (firstNonCatalogueLayer === titleid) {
              console.debug('getHashString - layer is first basemap layer: not adding to URL hash', titleid)

              continue
            }
          }

          // console.debug('getHashString - layers', layerConf.titleid)

          if (includes(hashValue, 'http')) {
            console.log('Always include full external url in url.. no way to generate this from config.')
          } else if (!layerConf) {
            console.error('Invalid layer?', hashValue)
            continue
          } else if (layerConf.selectedAtLoad) {
            continue // Skip adding default visible items to the URL
          }
        }

        hashValue = encodeURIComponent(hashValue)
        arr.push(getHashFragment(parentKey, hashKey, hashValue))
      }
    }
  }
  if (arr.length > 0) {
    arr.sort(sortHashArr)
    return '/' + arr.join('/') + '/'
  } else {
    return ''
  }
}

/**
 * Update URL with the current app.state, but only if the app state has changed
 * And make sure updates don't happen too frequently by using debounce
 */
export const HistoryUpdateURL = debounce(HistoryUpdateURLDebounced, 500)

export function HistoryUpdateURLDebounced (replace = false) {
  const newHash = getHashString()
  const previousHash = window.location.hash.replace(/^#/, '')
  const shouldUpdateHistory = newHash !== previousHash

  if (shouldUpdateHistory) {
    const action = {
      // pathname: '/',
      // search: search,
      hash: newHash
    }

    // TODO: determine when to use 'push' and when to use 'replace'
    if (replace) {
      // console.info('History REPLACE', newHash)
      history.replace(action)
    } else {
      // console.info('History PUSH', newHash)
      history.push(action)

      // Once the history
      historyUpdated = true
    }
  } else {
    // console.info('History not updating because hash is the same', newHash)
  }
}

const sortHashArr = function (a, b) {
  const order = ['site', 'x', 'y', 'z']
  for (const key in order) {
    const value = order[key]
    if (startsWith(a, value)) {
      return -1
    } else if (startsWith(b, value)) {
      return 1
    }
  }
  if (a === b) {
    return 0
  } else if (a < b) {
    return -1
  } else {
    return 1
  }
}

const getHashFragment = function (id, key, value) {
  if (!key || key === '') {
    return id + '[' + value + ']'
  }
  if (!value || value === '') {
    return id + '[' + key + ']'
  }
  return id + '[' + key + ',' + value + ']'
}
